/**
 * Fetches a file via the given request.
 *
 * Reads the filename from the response's Content-Disposition header, falling back to
 * `fallbackFilename` when that header is missing or doesn't match the expected format.
 * The resolved filename is exposed via `filename` once `perform()` settles, and is what
 * `write()` uses for the downloaded file's name.
 *
 * @example
 *   const download = new FileDownload(new FetchRequest('GET', '/exports/1.xlsx'), 'export.xlsx');
 *   // if you want to receive a blob
 *   download.perform().then((blob) => console.log(blob.size));
 *   // if you want the blob to be downloaded to the host machine
 *   download.write().then((blob) => console.log(blob.size))
 */
class FileDownload {
  /**
   * @param {FetchRequest} request
   * @param {String} fallbackFilename used when Content-Disposition is missing or unparseable
   */
  constructor(request, fallbackFilename) {
    this.request = request;
    this.fallbackFilename = fallbackFilename;
    this.filename = fallbackFilename;
  }

  /**
   * @return {Promise<Blob>}
   */
  perform() {
    return this.request.perform().then((response) => {
      if (!response.ok) {
        return Promise.reject(new Error(`Failed to download the file: ${response.statusCode}`));
      }

      this.filename = this.#filenameFrom(response);

      return response.response.blob();
    });
  }

  /**
   * Triggers the browser's native save-to-disk flow for downloaded blob.
   *
   * @return {Promise<Blob>}
   */
  write() {
    return this.perform().then((blob) => {
      const url = URL.createObjectURL(blob);
      const anchor = this.#buildAnchor(url);
      this.#issueDownload(anchor);
      anchor.remove();

      // Deferred rather than revoked right away - some browsers might need a moment to actually
      // start reading from the blob URL, and revoking it too early can interrupt the download.
      return new Promise((resolve) => {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          resolve(blob);
        }, 0);
      });
    });
  }

  #filenameFrom(response) {
    const disposition = response.headers.get('Content-Disposition') ?? '';

    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match) {
      return decodeURIComponent(utf8Match[1]);
    }

    const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
    return asciiMatch ? asciiMatch[1] : this.fallbackFilename;
  }

  #buildAnchor(href) {
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = this.filename;

    return anchor;
  }

  #issueDownload(anchor) {
    document.body.appendChild(anchor);
    anchor.click();
  }
}

export default FileDownload;
